import { getBannerLayoutIssue } from '../../../features/bannerLayout/issues';
import type { JSX } from 'react';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styles from '../GenerationWorkspace.module.css';
import { getResponseErrorMessage, resolveErrorMessage } from '../../../utils/getErrorMessage';
import '../../../styles/form-controls.css';
import '../../../styles/prompt-history.css';
import { addToPromptHistory, PROMPT_HISTORY_KEYS } from '../../../utils/promptHistory';
import { PromptHistoryModal } from '../../common/PromptHistoryModal';
import { useGenerationAssets } from '../state/assetsContext';
import { isStillImageUsableAsset, resolveVideoFrameRefUrl } from '../state/assetMediaUtils';
import { useGenerationJobs } from '../state/jobsContext';
import { useGenerationSettings } from '../state/settingsContext';
import {
  DEFAULT_GENERATION_MODEL,
  GENERATION_MODEL_SPEED,
  resolveLegacyImageModel,
  thinkingLevelPayload,
  DEFAULT_THINKING_LEVEL,
  type ThinkingLevel,
  DEFAULT_SETTINGS,
  normalizeOutputFormats,
  isTransparencyCapableModel,
  type ActiveJob,
} from '../state/useGenerationState';
import { useGenerationCredits, useCreditBadgeText } from '../hooks/useGenerationCredits';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { apiClient } from '../../../services/apiClient';
import { fetchMaterialById, captureUrlToTemporary } from '../../../features/materials/api';
import { isCaptureRequiredTemplateActive } from '../utils/templateCapture';
import { resolveMaterialUrls, buildDragData } from '../../../features/materials/materialUrl';
import type { GenerationAsset } from '../state/useGenerationState';
import { createImageGenerationJob, waitForJobCompletion, AsyncJobError, type ImageGenerationResult } from '../../../services/asyncJobApi';
import { MaterialPreviewModal } from '../../materials/shared/MaterialPreviewModal';
import { GenerationInfoModal } from '../../materials/shared/GenerationInfoModal';
import { GenerationInfoButton } from '../../materials/shared/GenerationInfoButton';
import { hasGenerationInfo, parseGenerationMetadata, resolveAttributeLabels, TEXT_ATTRIBUTE_KEYS } from '../../materials/shared/generationInfoUtils';
import type { AttributeNode } from '../../materials/shared/generationInfoUtils';
import { calculateAspectRatio } from '../../materials/shared/materialUtils';
import { usePromptAttributes } from '../hooks/usePromptAttributes';
import { GenerateButton } from '../common/GenerateButton';
import { Tooltip } from '../../common/Tooltip';
import { HelpTooltip } from '../../common/HelpTooltip';
import type { TemporaryMaterial } from '../../../features/materials/materialsSlice';
import { useStorageInfo } from '../../../features/organizations/hooks';
import { useCredits } from '../../../hooks/useCredits';
import { usePurchaseModal } from '../../../hooks/usePurchaseModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../../hooks/useToast';
import { isStorageQuotaError, storageQuotaErrorMessage } from '../../../utils/storageQuotaError';
import { brandConfig, isTemplateOnlyHome } from '../../../config/brands';
import { jobHistoryExpandedKey } from '../../../utils/jobHistoryStorage';
import type { UserInputs } from '../../../hooks/useGenerationJobs';

type GenerationJobResult = Record<string, unknown> | undefined;

type GenerationJob = {
  id: string;
  material_id?: string;
  status: string;
  created_at: string;
  model: string;
  asset_count: number;
  result?: GenerationJobResult;
  image_url?: string;
  thumbnail_url?: string;
  storage_bucket?: string;
  storage_path?: string;
  is_deleted?: boolean;
  error_message?: string;
  user_inputs?: UserInputs;
};

const JOB_HISTORY_EXPANDED_KEY = jobHistoryExpandedKey('image');

// ジョブ作成のみ（即座に返る）
async function submitGenerationJob(
  payload: Record<string, unknown>
): Promise<{ job_id: string }> {
  const { job_id } = await createImageGenerationJob(payload);
  return { job_id };
}

// 非同期ジョブAPIの結果をGenerationJob形式に変換
function convertToGenerationJob(result: ImageGenerationResult, jobId: string): GenerationJob {
  return {
    id: result.job_id || jobId,
    material_id: result.material_id || undefined,
    status: result.status === 'completed' ? 'completed' : result.status,
    created_at: new Date().toISOString(),
    model: result.model,
    asset_count: 1,
    image_url: result.image_url || undefined,
    storage_bucket: result.storage_bucket || undefined,
    storage_path: result.storage_path || undefined,
  };
}

const JOBS_PAGE_SIZE = 20;

async function fetchJobs(offset = 0, limit = JOBS_PAGE_SIZE) {
  const { data } = await apiClient.get<{ items: GenerationJob[]; has_more: boolean }>(
    `/generation/jobs?limit=${limit}&offset=${offset}&kind=image`,
  );
  return { items: data.items ?? [], hasMore: data.has_more ?? false };
}

async function fetchTemporaryMaterial(materialId: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.get<TemporaryMaterial>(`/materials/temporary/${materialId}`);
  return data;
}

const TIMEOUT_MESSAGE = '生成に時間がかかりすぎています。より小さい解像度を選択するか、しばらく待ってから再度お試しください。';

/**
 * 静止画生成のエラー表示。抽出は共通の resolveErrorMessage に任せ、
 * バックエンド由来のメッセージだけを内容に応じて言い換える
 * (axios 側の "Request failed with status code 503" 等を誤って
 * 「モデル混雑」と言い換えないよう、判定対象を分けている)。
 */
function resolveGenerationErrorMessage(error: unknown): string {
  const responseMessage = getResponseErrorMessage(error);

  if (responseMessage) {
    // 画像データなしエラーの場合
    if (responseMessage.includes('Gemini API returned no image data')) {
      return '画像を生成できませんでした。消費ポイントは返還されました。';
    }
    // 503エラー（モデル過負荷）の場合、ユーザーフレンドリーなメッセージを表示
    if (responseMessage.includes('overloaded') || responseMessage.includes('503')) {
      return 'AIモデルが混雑しています。自動的にリトライを試みましたが、失敗しました。しばらく待ってから再度お試しください。';
    }
    // 429エラー（レート制限）の場合
    if (responseMessage.includes('rate limit') || responseMessage.includes('429')) {
      return 'リクエスト数が上限に達しました。しばらく待ってから再度お試しください。';
    }
    // タイムアウトエラー
    if (responseMessage.includes('timed out') || responseMessage.includes('timeout')) {
      return TIMEOUT_MESSAGE;
    }
    return responseMessage;
  }

  const message = (error as { message?: string } | undefined)?.message;
  if (message) {
    return message.includes('timeout') ? TIMEOUT_MESSAGE : message;
  }
  // 何も取れない場合の最終手段 (空文字 / JSON.stringify) は共通実装に委ねる
  return resolveErrorMessage(error);
}

function isSexualContentNoImage(error: unknown): boolean {
  return error instanceof AsyncJobError && error.error_code === 'SEXUAL_CONTENT_NO_IMAGE';
}


type InlinePart = {
  inlineData?: {
    data?: string;
    mimeType?: string;
  };
  inline_data?: {
    data?: string;
    mime_type?: string;
  };
};

function resolveInlineMime(inline: InlinePart['inlineData'] | InlinePart['inline_data']): string {
  if (!inline) {
    return 'image/png';
  }
  if ('mimeType' in inline && inline.mimeType) {
    return inline.mimeType;
  }
  if ('mime_type' in inline && inline.mime_type) {
    return inline.mime_type;
  }
  return 'image/png';
}

function extractInlineImage(job: GenerationJob | null) {
  if (!job) return undefined;
  // image_urlを優先的に使用
  if (job.image_url) return job.image_url;
  // resultがない場合はここで終了（バックエンドがresultを除外している場合）
  if (!job.result) return undefined;
  const result = job.result as {
    candidates?: Array<{
      content?: { parts?: Array<InlinePart> };
      contents?: { parts?: Array<InlinePart> };
    }>;
  };
  const candidates = result.candidates || [];
  for (const candidate of candidates) {
    const content = candidate?.content || candidate?.contents;
    const parts = content?.parts || [];
    for (const part of parts) {
      const inline = part.inlineData || part.inline_data;
      if (inline?.data) {
        const mime = resolveInlineMime(inline);
        return `data:${mime};base64,${inline.data}`;
      }
    }
  }
  return undefined;
}

// 選択した素材からASINを抽出
function extractAsinsFromAssets(assets: any[]): string[] {
  const asinSet = new Set<string>();
  assets.forEach((asset) => {
    if (asset.asin) {
      if (Array.isArray(asset.asin)) {
        asset.asin.forEach((a: string) => {
          if (a) asinSet.add(a);
        });
      } else if (typeof asset.asin === 'string') {
        asinSet.add(asset.asin);
      }
    }
  });
  return Array.from(asinSet);
}

type GenerationPreviewPanelProps = {
  mode?: 'attributes' | 'prompt';
  onModeChange?: (mode: 'attributes' | 'prompt') => void;
  pendingPrompt?: string | null;
  onClearPendingEdit?: () => void;
  isProductInfoMissing?: boolean;
  thinkingLevel?: ThinkingLevel;
  /** プロンプトと生成ボタンの間に挿入するコンテンツ */
  beforeGenerateButton?: React.ReactNode;
  /** プロンプト入力モードで textarea の直下に出す要素 (ロゴを追加するトグル等) */
  promptFooter?: React.ReactNode;
  /** 生成ボタンが画面外に出たときの下部スティッキーバーを出すか (usodeki ホームは false) */
  stickyGenerateButton?: boolean;
};

// 属性がデフォルトから変更されているかを判定
// 属性値が「指定なし」以外に設定されているかを判定する。
// 文字列: 空でも 'none' でもない / 数値: 設定あり / 真偽値: true / オブジェクト: 中に設定済みの値がある
function isAttributeValueSet(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '' && value !== 'none';
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return value === true;
  if (Array.isArray(value)) return value.some((v) => isAttributeValueSet(v));
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some((v) => isAttributeValueSet(v));
  return false;
}

// 属性ツリー上の全ノードキーを収集する (DB 駆動属性を含む)。
function collectAttributeTreeKeys(nodes: AttributeNode[] | undefined, out: Set<string> = new Set()): Set<string> {
  if (!Array.isArray(nodes)) return out;
  for (const node of nodes) {
    if (node?.key) out.add(node.key);
    if (node?.children) collectAttributeTreeKeys(node.children, out);
  }
  return out;
}

function hasAttributesChanged(settings: any, attributeTree?: AttributeNode[]): boolean {
  const currentAttrs = settings.attributes || {};
  const defaultAttrs = DEFAULT_SETTINGS.attributes;

  // トップレベル属性のチェック (ツリー未ロード時のフォールバックを兼ねる)
  const topLevelKeys = ['background', 'scene', 'seasons_weather', 'hand', 'orientation', 'effect', 'overall_impression'];
  for (const key of topLevelKeys) {
    const current = currentAttrs[key];
    const defaultVal = (defaultAttrs as any)[key] ?? 'none';
    if (current && current !== 'none' && current !== defaultVal) {
      return true;
    }
  }

  // DB 駆動属性のチェック: 属性ツリー上のキーを動的に走査し、
  // カメラアングル等の OEM/動的に追加された属性が選択されていれば「変更あり」とみなす。
  // person は下の専用ロジックで、aspect_ratio は媒体要件由来なのでここでは扱わない。
  const treeKeys = collectAttributeTreeKeys(attributeTree);
  for (const key of treeKeys) {
    if (key === 'person' || key === 'aspect_ratio') continue;
    const current = currentAttrs[key];
    if (!isAttributeValueSet(current)) continue;
    const defaultVal = (defaultAttrs as any)[key];
    if (defaultVal !== undefined && current === defaultVal) continue;
    return true;
  }

  // バナーレイアウトを適用していれば設定ありとみなす
  if (settings.banner_layout) {
    return true;
  }

  // テキスト入力のチェック
  const textKeys = ['main_copy', 'cta', 'disclaimer_text', 'display_text', 'price_text', 'date_text', 'other_instructions'];
  for (const key of textKeys) {
    const current = currentAttrs[key];
    if (current && typeof current === 'string' && current.trim() !== '') {
      return true;
    }
  }

  // generate_text_from_ecのチェック (3値: true=生成する / 'never'=生成しない も設定ありとみなす)
  if (currentAttrs.generate_text_from_ec === true || currentAttrs.generate_text_from_ec === 'never') {
    return true;
  }

  // person属性のチェック
  const currentPerson = currentAttrs.person as Record<string, unknown> | undefined;
  const defaultPerson = defaultAttrs.person as Record<string, unknown>;
  if (currentPerson) {
    // 人物なし（none: true）以外の変更をチェック
    if (currentPerson.none === true) {
      // 「人物なし」は変更とみなす
      return true;
    }
    if (currentPerson.gender && currentPerson.gender !== 'none' && currentPerson.gender !== defaultPerson.gender) {
      return true;
    }
    if (currentPerson.ethnicity && currentPerson.ethnicity !== 'jp' && currentPerson.ethnicity !== defaultPerson.ethnicity) {
      return true;
    }
    if (currentPerson.age !== null && currentPerson.age !== undefined && currentPerson.age !== defaultPerson.age) {
      return true;
    }
    if (currentPerson.physique && currentPerson.physique !== 'none') {
      return true;
    }
    // build / appearance は physique (体格・身体的特徴) 統合前の旧キー (生成履歴の復元で残る)
    if (currentPerson.build && currentPerson.build !== 'none') {
      return true;
    }
    if (currentPerson.appearance && currentPerson.appearance !== 'none') {
      return true;
    }
    if (currentPerson.expression && currentPerson.expression !== 'none') {
      return true;
    }
    if (currentPerson.fashion && currentPerson.fashion !== 'none' && currentPerson.fashion !== defaultPerson.fashion) {
      return true;
    }
    if (currentPerson.shot && currentPerson.shot !== 'none' && currentPerson.shot !== defaultPerson.shot) {
      return true;
    }
  }

  // モデル選択のチェック
  if (settings.model) {
    return true;
  }

  // ロゴ追加のチェック
  if (settings.include_logo) {
    return true;
  }

  // テキストを含めないオプションのチェック
  if (settings.exclude_text_response) {
    return true;
  }

  return false;
}

export function GenerationPreviewPanel({ mode = 'attributes', onModeChange, pendingPrompt, onClearPendingEdit, isProductInfoMissing, thinkingLevel = DEFAULT_THINKING_LEVEL, beforeGenerateButton, promptFooter, stickyGenerateButton = true }: GenerationPreviewPanelProps) {
  const { settings, attributeTree, setSettings, restoreSettings, templateReferenceUrl, setTemplateReferenceUrl } = useGenerationSettings();
  const { selectedAssets, requestOpenPersonalFolder, replaceAssets, addAsset } = useGenerationAssets();
  const { activeJobs, setActiveJobs, setShowJobQueueModal } = useGenerationJobs();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Sticky表示の有効条件：素材が1つ以上ある、またはAI俳優が選択されている場合（属性選択モードのみ）
  const stickyEnabled = useMemo(() => {
    if (!stickyGenerateButton) return false;
    if (mode !== 'attributes') return false;
    return selectedAssets.length > 0 || !!settings.model;
  }, [stickyGenerateButton, mode, selectedAssets.length, settings.model]);

  // ボタンが画面外に出たかどうかを監視
  const buttonSectionRef = useRef<HTMLDivElement>(null);
  const [isButtonOutOfView, setIsButtonOutOfView] = useState(false);

  useEffect(() => {
    if (!stickyEnabled) {
      setIsButtonOutOfView(false);
      return;
    }

    const buttonSection = buttonSectionRef.current;
    if (!buttonSection) return;

    // PCとスマホで異なるrootMarginを使用
    // PC: ボタンが見えたらすぐにstickyを消す（0px）
    // スマホ: フッターとの兼ね合いで少し余裕を持たせる（-150px）
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const rootMarginBottom = isMobile ? '-150px' : '-95px';

    const observer = new IntersectionObserver(
      ([entry]) => {
        // ボタンが画面内に見えていない場合にtrue
        setIsButtonOutOfView(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${rootMarginBottom} 0px`,
      }
    );

    observer.observe(buttonSection);

    return () => {
      observer.disconnect();
    };
  }, [stickyEnabled]);

  // Stickyボタンを表示する条件：有効かつボタンが画面外
  const showStickyButton = stickyEnabled && isButtonOutOfView;
  const [lastJob, setLastJob] = useState<GenerationJob | null>(null);
  const [materialModalId, setMaterialModalId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [imagePrompt, setImagePrompt] = useState('');
  const { data: creditsData } = useCredits();
  const { openPurchaseModal, purchaseModals } = usePurchaseModal();
  const { data: storageData } = useStorageInfo();

  // LocalStorageから画像生成モデルと解像度を読み込み、初回マウント時に設定
  useEffect(() => {
    const rawSavedModel = localStorage.getItem('generation_model');

    // 廃止済みモデル (gpt-image-2-* / gemini-3.1-flash-image-preview / gemini-2.5-flash-image) の
    // 旧値は現行コードへ読み替えて保存し直す (IMAGE_MODEL_LEGACY_MAP)
    const savedModel = resolveLegacyImageModel(rawSavedModel);
    if (savedModel && savedModel !== rawSavedModel) {
      localStorage.setItem('generation_model', savedModel);
    }

    // 旧 image_size キーからモード別キーへのマイグレーション
    const legacyImageSize = localStorage.getItem('image_size');
    if (legacyImageSize) {
      if (savedModel === GENERATION_MODEL_SPEED) {
        if (!localStorage.getItem('image_size_speed')) {
          localStorage.setItem('image_size_speed', legacyImageSize);
        }
      } else {
        if (!localStorage.getItem('image_size_quality')) {
          localStorage.setItem('image_size_quality', legacyImageSize);
        }
      }
      localStorage.removeItem('image_size');
    }

    // 既存のスピードモードユーザーにはフラグを立てておく
    if (savedModel === GENERATION_MODEL_SPEED && !localStorage.getItem('has_used_speed_mode')) {
      localStorage.setItem('has_used_speed_mode', '1');
    }

    setSettings((prev) => {
      const updates: any = {};

      // モデルの設定
      if (savedModel && !prev.generation_model) {
        updates.generation_model = savedModel;
      } else if (!prev.generation_model) {
        // デフォルトは高品質モード
        updates.generation_model = DEFAULT_GENERATION_MODEL;
      }

      // 解像度の設定（モード別にlocalStorageから読み込み）
      const finalModel = updates.generation_model || prev.generation_model;
      const sizeKey = finalModel === GENERATION_MODEL_SPEED ? 'image_size_speed' : 'image_size_quality';
      let savedImageSize = localStorage.getItem(sizeKey);
      // 512px は 2026-10-08 に廃止。保存済みの 512px は 1K へ読み替えて保存し直す
      if (savedImageSize === '512px') {
        savedImageSize = '1K';
        localStorage.setItem(sizeKey, savedImageSize);
      }
      if (savedImageSize && !prev.image_size) {
        updates.image_size = savedImageSize;
      } else if (!prev.image_size) {
        // デフォルト: 1K (GPT Image 2 は 1K → 2K で料金が数倍跳ね上がるため、
        // 明示的に 2K/4K を選ばない限り低コストの 1K を既定値とする)
        updates.image_size = '1K';
      }

      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [setSettings]);

  // 画像生成・ドロップダウン表示用のクレジットレートは共通フックへ集約
  const { imageCredits } = useGenerationCredits();
  const { data: imageModels = [] } = useGenerationModels('image');

  const [isPromptSectionOpen, setIsPromptSectionOpen] = useState(true); // プロンプト入力セクション（デフォルトオープン）
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

  // プロンプト履歴 (全画面モーダル。docs/features/prompt-history-db-plan.md)
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const generatedPreviewRef = useRef<HTMLElement>(null);
  const resultSectionRef = useRef<HTMLElement>(null);

  // 履歴からプロンプトを選択 (モーダル側で選択と同時にモーダルを閉じる)
  const handleSelectPromptHistory = useCallback((prompt: string) => {
    setImagePrompt(prompt);
  }, [setImagePrompt]);

  // 編集リクエスト (pendingPrompt) を受け取ったら imagePrompt に反映してクリアする。
  // 素材の「AIに修正依頼」は本文を持たない空文字で来る (入力中のプロンプトは残す) が、クリアは必ず行う。
  // 空文字でクリアしないと pendingMode が 'prompt' のまま残り、同じタブでの2回目以降の「AIに修正依頼」で
  // setPendingMode('prompt') が同値更新となってモード切替 (useStudioRouting) が走らなくなる
  useEffect(() => {
    if (pendingPrompt === null || pendingPrompt === undefined) return;
    if (pendingPrompt) {
      setImagePrompt(pendingPrompt);
    }
    onClearPendingEdit?.();
  }, [pendingPrompt, onClearPendingEdit]);

  // プロンプトモードでtextareaに自動フォーカス（PC時のみ）
  useEffect(() => {
    if (mode === 'prompt' && promptTextareaRef.current) {
      const isMobileView = window.matchMedia('(max-width: 768px)').matches;
      if (isMobileView) return;
      setTimeout(() => {
        promptTextareaRef.current?.focus();
      }, 100);
    }
  }, [mode, imagePrompt]);

  // 選択した素材からASINを抽出
  const extractedAsins = useMemo(() => extractAsinsFromAssets(selectedAssets), [selectedAssets]);
  const asinValue = extractedAsins.length > 0 ? extractedAsins : undefined;

  const [jobsOffset, setJobsOffset] = useState(0);
  const [allJobs, setAllJobs] = useState<GenerationJob[]>([]);
  const [jobsHasMore, setJobsHasMore] = useState(false);
  const jobsQuery = useQuery({
    queryKey: ['generation', 'jobs', 'image', jobsOffset],
    queryFn: () => fetchJobs(jobsOffset),
    staleTime: 30_000,
  });

  // ページ取得完了時にジョブを蓄積
  useEffect(() => {
    if (!jobsQuery.data) return;
    const { items, hasMore } = jobsQuery.data;
    setJobsHasMore(hasMore);
    setAllJobs((prev) => {
      if (jobsOffset === 0) return items;
      const existingIds = new Set(prev.map((j) => j.id));
      return [...prev, ...items.filter((j) => !existingIds.has(j.id))];
    });
  }, [jobsQuery.data, jobsOffset]);

  const handleLoadMoreJobs = useCallback(() => {
    setJobsOffset((prev) => prev + JOBS_PAGE_SIZE);
  }, []);

  const handleRefreshJobs = useCallback(async () => {
    setJobsOffset(0);
    await jobsQuery.refetch();
  }, [jobsQuery]);

  // 素材データを取得するクエリ（個人フォルダから取得）
  const materialQuery = useQuery({
    queryKey: ['temporary-material', materialModalId],
    queryFn: async () => {
      // Try to get from cache first
      const cachedMaterials = queryClient.getQueryData<{ items: TemporaryMaterial[] }>(['materials', 'temporary']);
      if (cachedMaterials?.items) {
        const found = cachedMaterials.items.find(m => m.id === materialModalId);
        if (found) return found;
      }

      // Fallback: fetch from API
      return fetchTemporaryMaterial(materialModalId!);
    },
    enabled: !!materialModalId,
  });

  // 生成直後プレビュー用: lastJob の素材データ（AI生成情報ボタン表示判定・モーダル表示に使用）
  const [showGenerationInfo, setShowGenerationInfo] = useState(false);
  const lastJobMaterialId = lastJob?.material_id;
  const lastJobMaterialQuery = useQuery({
    queryKey: ['temporary-material', lastJobMaterialId],
    queryFn: async () => {
      const cachedMaterials = queryClient.getQueryData<{ items: TemporaryMaterial[] }>(['materials', 'temporary']);
      if (cachedMaterials?.items) {
        const found = cachedMaterials.items.find(m => m.id === lastJobMaterialId);
        if (found) return found;
      }
      return fetchTemporaryMaterial(lastJobMaterialId!);
    },
    enabled: !!lastJobMaterialId,
  });
  const lastJobMaterial = lastJobMaterialQuery.data;
  const lastJobHasGenerationInfo = hasGenerationInfo(
    (lastJobMaterial as Record<string, unknown> | undefined)?.generation_metadata as Record<string, unknown> | undefined
  );

  const [infoModalMaterialId, setInfoModalMaterialId] = useState<string | null>(null);

  const [showOlderJobs, setShowOlderJobsState] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(JOB_HISTORY_EXPANDED_KEY) : null;
    if (saved !== null) return saved === '1';
    // HypeApp はデフォルトでエキスパンド
    return brandConfig.id === 'hypeapp';
  });
  const setShowOlderJobs = useCallback((updater: boolean | ((prev: boolean) => boolean)) => {
    setShowOlderJobsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem(JOB_HISTORY_EXPANDED_KEY, next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  }, []);
  const isMountedRef = useRef(true);

  const activeJobCount = activeJobs.filter(j => j.status === 'polling').length;

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const sortedJobs = useMemo(() => {
    if (!allJobs.length) {
      return [] as GenerationJob[];
    }
    return [...allJobs]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [allJobs]);


  const jobMutation = useMutation({
    mutationFn: submitGenerationJob, // ジョブ作成のみ（数秒で完了）
    onSuccess: ({ job_id }) => {
      // クレジット残高を即時更新（バックエンドでデポジット済み）
      queryClient.invalidateQueries({ queryKey: ['credits'] });

      // アクティブジョブリストに追加
      const newJob: ActiveJob = { jobId: job_id, status: 'polling', startedAt: Date.now(), type: 'image' };
      setActiveJobs(prev => [...prev, newJob]);

      // バックグラウンドでポーリング開始（awaitしない）
      waitForJobCompletion<ImageGenerationResult>(job_id, {
        maxDuration: 10 * 60 * 1000,
        // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
        skipVisibilityCheck: true,
      })
        .then((asyncJob) => {
          const job = convertToGenerationJob(asyncJob.result_data, job_id);
          // Context状態の更新（アンマウント後も実行可能）
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'completed' as const, result: job } : j
          ));
          queryClient.setQueryData<GenerationJob[] | undefined>(['generation', 'jobs'], (previous) => {
            const list = previous ? [...previous] : [];
            const index = list.findIndex((item) => item.id === job.id);
            if (index >= 0) {
              list[index] = job;
            } else {
              list.unshift(job);
            }
            return list;
          });
          handleRefreshJobs();
          queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
          queryClient.invalidateQueries({ queryKey: ['credits'] });
          requestOpenPersonalFolder();
          toast.success('画像の生成が完了しました');

          // ローカルstate・DOM操作はマウント中のみ
          if (isMountedRef.current) {
            setLastJob(job);
            setTimeout(() => {
              generatedPreviewRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }, 100);
          }
        })
        .catch((error) => {
          // Context状態の更新（アンマウント後も実行可能）
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'failed' as const, error: resolveGenerationErrorMessage(error) } : j
          ));
          if (isStorageQuotaError(error)) {
            toast.error(storageQuotaErrorMessage());
          } else {
            toast.error(resolveGenerationErrorMessage(error));
          }

          // ローカル操作はマウント中のみ
          if (isMountedRef.current) {
            if (isSexualContentNoImage(error) && mode === 'prompt') {
              navigate(`${location.pathname}?guide=attributes`);
            }
          }
        });
    },
    onError: () => {
      // ジョブ作成自体の失敗（ネットワークエラー等）
      setTimeout(() => {
        resultSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    },
  });

  const preparedAssets = useMemo(
    () =>
      selectedAssets
        // 音声と「動画そのまま」の動画は静止画生成に使えないため除外する
        // (動画プロンプト入力モードとスコープ共有のため選択に残り得る)。
        // フレーム指定 (最初/最後のフレーム) の動画は抽出済みフレーム画像を参考画像に使う。
        .filter(isStillImageUsableAsset)
        .map((asset) => {
          const frameUrl = resolveVideoFrameRefUrl(asset);
          return {
            // id/type は選択した素材のまま送る (商品リンク解決は素材基準)。
            id: asset.id,
            // sourceUrlを優先、なければthumbnailUrlを使用
            public_url: frameUrl ?? (asset.sourceUrl || asset.thumbnailUrl || undefined),
            // storage情報も送信（バックエンドでpublic_urlがなくても取得可能）。
            // フレーム差し替え時は動画本体を指すため送らない。
            storage_bucket: frameUrl ? undefined : (asset.storageBucket || undefined),
            storage_path: frameUrl ? undefined : (asset.storagePath || undefined),
            type: asset.type,
            asin: asset.asin || undefined,
          };
        })
        .filter((asset) => !!asset.public_url || (!!asset.storage_bucket && !!asset.storage_path)),
    [selectedAssets]
  );

  const hasAssets = preparedAssets.length > 0;

  const payload = useMemo(
    () => {
      const basePayload: any = {
        assets: preparedAssets,
        aspect_ratio: settings.aspect_ratio,
        asin: asinValue,
      };

      // 新しいパラメータを追加
      if (settings.generation_model) {
        basePayload.model = settings.generation_model;
      }
      // 画像生成モデルが解像度をサポートする場合 (supported_resolutions が非NULL) のみ image_size を送信
      const activeModelCode = settings.generation_model || DEFAULT_GENERATION_MODEL;
      const activeModel = imageModels.find((m) => m.model_code === activeModelCode);
      const modelSupportsResolution = !!activeModel?.supported_resolutions && activeModel.supported_resolutions.length > 0;
      if (modelSupportsResolution && settings.image_size) {
        basePayload.image_size = settings.image_size;
      }
      // 出力フォーマット: ユーザーが明示選択 (settings.output_format !== undefined) かつ
      // モデルがサポートしている場合のみ送信。「指定なし」はバックエンドの既定値に委ねる。
      const supportedFormats = normalizeOutputFormats(activeModel?.supported_output_formats ?? null);
      const requested = settings.output_format;
      if (requested && supportedFormats.includes(requested)) {
        basePayload.output_format = requested;
      }
      // 背景透過: 透過対応モデル (GPT Image 系) + PNG/WebP のときのみ 'background' を送信。
      // フロント状態は 'image_background' で持ち、OpenAI API パラメータ名 'background' にマップ。
      if (
        settings.image_background === 'transparent'
        && requested
        && (requested === 'png' || requested === 'webp')
        && isTransparencyCapableModel(activeModelCode)
      ) {
        basePayload.background = 'transparent';
      }
      if (settings.exclude_text_response) {
        basePayload.response_modalities = ['IMAGE'];
      }
      if (settings.include_logo) {
        basePayload.include_logo = true;
      }
      // プロンプト入力モードの「AIに送る情報」トグル (属性モードは自動で渡すので送らない)
      if (mode === 'prompt') {
        if (settings.include_product_info) {
          basePayload.include_product_info = true;
        }
        if (settings.include_brand_info) {
          basePayload.include_brand_info = true;
        }
      }
      // auto_trim_resolutionを明示的に送信（デフォルトtrue）
      basePayload.auto_trim_resolution = settings.auto_trim_resolution !== false;

      // auto-trim 時の目標解像度 (spec 派生 or ユーザー入力)。
      // 両方揃っている場合のみ送信し、バックエンドはこれを最優先で採用する。
      if (settings.target_width && settings.target_height) {
        basePayload.target_width = settings.target_width;
        basePayload.target_height = settings.target_height;
      }

      basePayload.media_platform_id = settings.media_platform_id ?? null;
      basePayload.media_menu_id = settings.media_menu_id ?? null;
      basePayload.media_spec_id = settings.media_spec_id ?? null;
      const menuGuidelines = settings.media_menu_guidelines || [];
      const specGuidelines = settings.media_guidelines || [];
      if ((menuGuidelines.length || specGuidelines.length) && mode === 'prompt') {
        basePayload.media_guidelines = [...menuGuidelines, ...specGuidelines];
      }

      // プロンプト入力モードの場合はpromptを追加、そうでない場合はsettingsを追加
      if (mode === 'prompt') {
        basePayload.prompt = imagePrompt;
      } else {
        // バナーレイアウトは settings に入れず payload 直下で送る (BE の受付検証・ガイド描画の正本)
        const { banner_layout: bannerLayout, ...settingsWithoutLayout } = settings;
        basePayload.settings = settingsWithoutLayout;
        if (menuGuidelines.length || specGuidelines.length) {
          basePayload.settings = {
            ...settingsWithoutLayout,
            media_guidelines: [...menuGuidelines, ...specGuidelines],
          };
        }
        if (bannerLayout) {
          basePayload.banner_layout = bannerLayout;
        }
      }

      // 思考レベル対応モデル (Gemini 標準 / スピード) のみ、そのモデルが受け付ける値で thinking_level を追加
      const resolvedThinkingLevel = thinkingLevelPayload(settings.generation_model || DEFAULT_GENERATION_MODEL, thinkingLevel);
      if (resolvedThinkingLevel) {
        basePayload.thinking_level = resolvedThinkingLevel;
      }

      // コンプラ ルール別 ON/OFF: ユーザーがスタジオで明示的に切り替えた値を payload に
      // 載せる。ルールが含まれていない (= ユーザー未操作) ものは backend で自動判定 +
      // 商品デフォルトにフォールバックする。
      if (settings.compliance_overrides) {
        const cleaned: Record<string, boolean> = {};
        for (const [code, val] of Object.entries(settings.compliance_overrides)) {
          if (typeof val === 'boolean') cleaned[code] = val;
        }
        if (Object.keys(cleaned).length > 0) {
          basePayload.compliance_overrides = cleaned;
        }
      }

      return basePayload;
    },
    [
      mode,
      imagePrompt,
      settings,
      preparedAssets,
      asinValue,
      thinkingLevel,
    ]
  );

  const isCreditInsufficient = useMemo(() => {
    if (!creditsData) return false;
    if (imageCredits === null) return false;
    if (imageCredits <= 0) return false;
    const totalAvailable = creditsData.total_available ?? creditsData.credits_balance;
    return totalAvailable < imageCredits;
  }, [creditsData, imageCredits]);

  // ストレージ容量超過チェック
  const isStorageExceeded = useMemo(() => {
    if (!storageData) return false;
    return storageData.is_over_limit;
  }, [storageData]);

  const attributesChanged = useMemo(() => hasAttributesChanged(settings, attributeTree as unknown as AttributeNode[]), [settings, attributeTree]);

  // キャプチャ必須テンプレート (ポスター等): URL 入力 or 参照画像素材のどちらかが必須。
  // URL があれば生成開始時にフルページキャプチャを素材登録して参照画像に加える。
  const captureTemplateActive = useMemo(
    () => mode === 'attributes' && isCaptureRequiredTemplateActive(attributeTree, settings.attributes),
    [mode, attributeTree, settings.attributes],
  );
  const templateCaptureUrl = captureTemplateActive ? templateReferenceUrl.trim() : '';
  const isTemplateReferenceMissing = captureTemplateActive && !hasAssets && !templateCaptureUrl;
  const [isCapturing, setIsCapturing] = useState(false);

  // 適用中のバナーレイアウトで生成できない理由 (比率のずれ・商品枠の素材が外れた)。属性モードだけ
  const bannerLayoutIssue = useMemo(
    () => (mode === 'prompt' ? null : getBannerLayoutIssue(settings.banner_layout, settings)),
    [mode, settings, preparedAssets],
  );

  const isInputMissing = useMemo(() => {
    if (mode === 'prompt') {
      return !imagePrompt.trim();
    }
    // AI俳優が選択されていれば素材なしでも生成可能。
    // キャプチャ必須テンプレートは URL があれば素材なしでも可 (生成時に素材化される)
    const hasModel = !!settings.model;
    return (!hasAssets && !hasModel && !templateCaptureUrl) || !attributesChanged;
  }, [mode, imagePrompt, hasAssets, attributesChanged, settings.model, templateCaptureUrl]);

  const creditBadgeText = useCreditBadgeText(imageCredits);

  const handleRestoreSettings = useCallback((genMeta: Record<string, unknown>) => {
    const parsed = parseGenerationMetadata(genMeta);
    if (!parsed) return;
    if (parsed.mode === 'attributes' && parsed.attributes) {
      const attrs = { ...parsed.attributes };
      const modelId = (attrs.model_id as string) || undefined;
      delete attrs.model_id;
      delete attrs.actor_name;
      // トップレベル設定（媒体要件ID・生成モデル・解像度等）は settingsSnapshot から復元
      const snapshot = (parsed.settingsSnapshot ?? {}) as Partial<typeof settings>;
      restoreSettings({
        ...snapshot,
        attributes: attrs as Record<string, unknown>,
        model: modelId,
      });
      onModeChange?.('attributes');
    } else if (parsed.mode === 'prompt') {
      if (parsed.directPrompt) {
        setImagePrompt(parsed.directPrompt);
      }
      if (parsed.settingsSnapshot) {
        // プロンプトモードでも媒体要件・生成モデル等は復元する
        const snapshot = parsed.settingsSnapshot as Partial<typeof settings>;
        restoreSettings({ ...snapshot, attributes: settings.attributes });
      } else if (parsed.directPrompt) {
        // 削除済み/失敗ジョブのフォールバック、または旧データ：媒体要件等は復元できない
        toast.show('プロンプトのみ復元しました');
      }
      onModeChange?.('prompt');
    }

    // 選択素材の復元: ID ベースで材料を再取得してスコープを置き換える。
    // sourceMaterialIds=null は旧データ (情報なし) なので現在の選択をそのまま保持する。
    const ids = parsed.sourceMaterialIds;
    if (ids === null) {
      // 旧データ: 復元できる情報が無いのでタッチしない
    } else if (ids.length === 0) {
      replaceAssets([]);
    } else {
      void (async () => {
        const results = await Promise.all(ids.map((id) => fetchMaterialById(id).catch(() => null)));
        const restored: GenerationAsset[] = [];
        for (const mat of results) {
          if (!mat) continue;
          const urls = resolveMaterialUrls(mat);
          restored.push(buildDragData(mat, urls) as unknown as GenerationAsset);
        }
        replaceAssets(restored);
        const missing = ids.length - restored.length;
        if (missing > 0) {
          toast.show(`一部の素材は復元できませんでした（削除済み: ${missing}件）`);
        }
      })();
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [restoreSettings, setImagePrompt, onModeChange, replaceAssets, toast]);

  const guidanceMessages = useMemo(() => {
    const messages: Array<string | JSX.Element> = [];
    if (isTemplateReferenceMissing) {
      // テンプレート特化ホームは URL フォームが常時出ているため、この案内は冗長で出さない
      if (!isTemplateOnlyHome) {
        messages.push('テンプレートにURLを入力、または画面キャプチャ画像を素材に追加してください。');
      }
    } else if (mode !== 'prompt' && !hasAssets && !templateCaptureUrl) {
      // テンプレート特化ホームは「①作りたいものを選ぶ」を先に促す導線なので、素材パネル案内は出さない
      if (!isTemplateOnlyHome) {
        messages.push('素材パネルから画像を選択してください。');
      }
    } else if (mode !== 'prompt' && (hasAssets || templateCaptureUrl) && !attributesChanged) {
      messages.push('属性を選択してください。');
    } else if (mode === 'prompt' && !imagePrompt.trim()) {
      messages.push('プロンプトを入力してください。');
    }
    if (bannerLayoutIssue) {
      messages.push(bannerLayoutIssue);
    }
    if (isCreditInsufficient) {
      messages.push(
        <span key="credits">
          ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。
        </span>
      );
    }
    if (isStorageExceeded) {
      messages.push(
        <span key="storage">
          ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。
          プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a>
        </span>
      );
    }
    return messages;
  }, [hasAssets, attributesChanged, imagePrompt, isCreditInsufficient, isStorageExceeded, mode, openPurchaseModal, isTemplateReferenceMissing, templateCaptureUrl, bannerLayoutIssue]);

  const handleGenerate = async () => {
    // プロンプト入力モードでは素材なしでも送信可能（裏機能）
    if (isInputMissing) return;
    if (isCreditInsufficient) return;
    if (isStorageExceeded) return;
    if (isTemplateReferenceMissing) return;
    if (bannerLayoutIssue) return;
    if (isCapturing) return;

    // キャプチャ必須テンプレート (ポスター等) で URL が入力されていれば、先に
    // フルページキャプチャを個人フォルダへ素材登録し、今回の生成の参照画像に加える。
    // selectedAssets への追加は state 反映が生成ペイロード構築に間に合わないため、
    // payload へは capturedAssets として直接マージする。
    let capturedAssets: typeof preparedAssets = [];
    if (templateCaptureUrl) {
      setIsCapturing(true);
      try {
        const material = await captureUrlToTemporary(templateCaptureUrl);
        const urls = resolveMaterialUrls(material);
        const asset = buildDragData(material, urls) as unknown as GenerationAsset;
        addAsset(asset, { silent: true });
        setTemplateReferenceUrl('');
        queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
        capturedAssets = [
          {
            id: asset.id,
            public_url: asset.sourceUrl || asset.thumbnailUrl || undefined,
            storage_bucket: asset.storageBucket || undefined,
            storage_path: asset.storagePath || undefined,
            type: asset.type,
            asin: undefined,
          },
        ];
      } catch (error) {
        toast.error(resolveGenerationErrorMessage(error));
        return;
      } finally {
        setIsCapturing(false);
      }
    }

    // 送信時点で履歴に追加（エラー時も含む）
    if (mode === 'prompt' && imagePrompt.trim()) {
      addToPromptHistory(PROMPT_HISTORY_KEYS.IMAGE_GENERATION, imagePrompt);
    } else if (mode === 'attributes') {
      const otherInstructions = (settings.attributes?.other_instructions as string | undefined) ?? '';
      if (otherInstructions.trim()) {
        addToPromptHistory(PROMPT_HISTORY_KEYS.IMAGE_GENERATION_ATTRIBUTES, otherInstructions);
      }
    }

    jobMutation.mutate(
      capturedAssets.length > 0
        ? { ...payload, assets: [...payload.assets, ...capturedAssets] }
        : payload,
    );
  };

  // トップページから state.autoGenerate=true で遷移してきた場合、
  // pendingPrompt が imagePrompt に反映され次第 1 度だけ自動生成を発火する
  const autoGenerateTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoGenerateTriggeredRef.current) return;
    if (mode !== 'prompt') return;
    if (!location.state || !(location.state as { autoGenerate?: boolean }).autoGenerate) return;
    if (!imagePrompt.trim()) return;
    if (jobMutation.isPending) return;
    autoGenerateTriggeredRef.current = true;
    // history state を消費（リロード時の再発火防止）
    window.history.replaceState({}, '');
    handleGenerate();
  }, [imagePrompt, mode, location.state, jobMutation.isPending]);

  const generatedImageSrc = useMemo(() => extractInlineImage(lastJob), [lastJob]);


  const handleOpenMaterialModal = (materialId?: string) => {
    if (!materialId) return;
    setMaterialModalId(materialId);
  };

  const handleCloseMaterialModal = () => {
    setMaterialModalId(null);
  };

  // キューウィンドウからの「表示」ボタン連携
  useEffect(() => {
    const handler = (e: Event) => {
      const materialId = (e as CustomEvent).detail?.materialId;
      if (materialId) handleOpenMaterialModal(materialId);
    };
    window.addEventListener('open-material-preview', handler);
    return () => window.removeEventListener('open-material-preview', handler);
  }, []);

  return (
    <>
      {/* プロンプト入力モード用のアコーディオン */}
      {mode === 'prompt' && (
        <div className={`accordion-section ${isPromptSectionOpen ? 'accordion-section-open' : ''}`}>
          <button
            type="button"
            className="accordion-header"
            onClick={() => setIsPromptSectionOpen(!isPromptSectionOpen)}
            aria-expanded={isPromptSectionOpen}
          >
            <span className="accordion-title">
              <img src="/content_pen_write.svg" alt="" className="accordion-title-icon" />
              <span>プロンプト</span>
            </span>
            <span className={`accordion-icon ${isPromptSectionOpen ? 'accordion-icon-open' : ''}`}>
              {isPromptSectionOpen ? '▼' : '▶'}
            </span>
          </button>
          <div className={`accordion-panel ${isPromptSectionOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
            <div className="prompt-history-container">
              <div className="prompt-label-row">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="gen-label-text">プロンプト</span>
                  <HelpTooltip contentKey="promptImage" ariaLabel="プロンプトのヘルプ" placement="bottom" />
                </span>
                <button
                  type="button"
                  className="prompt-history-toggle"
                  onClick={() => setShowPromptHistory(!showPromptHistory)}
                >
                  <span aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
                  履歴
                </button>
              </div>
              <textarea
                ref={promptTextareaRef}
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                rows={6}
                placeholder="画像生成用のプロンプトを入力してください"
                disabled={jobMutation.isPending}
                className="gen-textarea"
              />
              {promptFooter && (
                <div style={{ marginTop: '8px' }}>{promptFooter}</div>
              )}

              {/* プロンプト履歴モーダル (全画面) */}
              <PromptHistoryModal
                isOpen={showPromptHistory}
                onClose={() => setShowPromptHistory(false)}
                scope="image_prompt"
                storageKey={PROMPT_HISTORY_KEYS.IMAGE_GENERATION}
                onSelect={handleSelectPromptHistory}
                title="プロンプト履歴"
              />
            </div>
          </div>
        </div>
      )}

      {beforeGenerateButton}

      {/* 生成ボタンセクション */}
      <section className={styles.section} ref={buttonSectionRef}>
        <div className={styles.generationActions}>
          <div className={styles.actionsLeft}>
            {/* テンプレート特化ホームは「生成完了」ボタンを出さない (生成結果は下部プレビューで確認) */}
            {activeJobCount === 0 && activeJobs.length > 0 && !isTemplateOnlyHome && (
              <button
                type="button"
                className={styles.completedBtn}
                onClick={() => setShowJobQueueModal(true)}
              >
                ✓ 生成完了
              </button>
            )}
          </div>

          <div className={styles.actionsCenter}>
            <GenerateButton
              label={lastJob || activeJobs.length > 0 ? '画像を再生成' : '画像を生成'}
              creditCost={creditBadgeText}
              onClick={handleGenerate}
              isLoading={jobMutation.isPending || isCapturing}
              loadingLabel={isCapturing ? 'キャプチャ中' : '送信中'}
              showLoadingDots
              disabled={
                jobMutation.isPending
                || isCapturing
                || isInputMissing
                || isTemplateReferenceMissing
                || !!bannerLayoutIssue
                || isCreditInsufficient
                || isStorageExceeded
                || isProductInfoMissing
              }
            />
          </div>

          <div className={styles.actionsRight}>
            {guidanceMessages.length > 0 && (
              <div className={styles.generateGuidance}>
                {guidanceMessages.map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* エラー表示セクション（ジョブ作成APIエラー時のみ） */}
      {jobMutation.isError && (
        <section ref={resultSectionRef} className={styles.section}>
          <div className={styles.error}>
            <p>{resolveGenerationErrorMessage(jobMutation.error)}</p>
          </div>
        </section>
      )}

      {/* 生成プレビューセクション（画像生成完了後のみ表示） */}
      {lastJob && (
        <section ref={generatedPreviewRef} className={styles.section}>
          {generatedImageSrc ? (
            <div className={styles.generatedPreview}>
              <button
                type="button"
                className={styles.generatedPreviewButton}
                onClick={() => handleOpenMaterialModal(lastJob?.material_id)}
                aria-label="生成結果を拡大表示"
              >
                <img src={generatedImageSrc} alt="生成結果" />
              </button>
              <div className={styles.generatedPreviewNote}>
                <span style={{ fontSize: '11px', color: '#666' }}>個人フォルダに保存済み</span>
                {lastJobHasGenerationInfo && (
                  <GenerationInfoButton onClick={() => setShowGenerationInfo(true)} />
                )}
              </div>
            </div>
          ) : (
            <div className={styles.selectionList}>
              <span className={styles.empty}>生成結果に画像は含まれていません。</span>
            </div>
          )}
        </section>
      )}

      {/* 生成履歴セクション */}
      <section className={styles.section}>
        {jobsQuery.isError ? (
          <div className={styles.error}>生成履歴の取得に失敗しました</div>
        ) : (
          <div className={styles.jobHistory}>
            <div className={styles.historyCollapse}>
              <button
                type="button"
                className={`${styles.historyToggle} ${showOlderJobs ? styles.historyToggleExpandedTop : ''}`}
                onClick={() => setShowOlderJobs((prev) => !prev)}
                disabled={jobsQuery.isLoading}
              >
                <img src="/time_clock_circle.svg" alt="生成履歴" className={styles.historyIcon} />
                <span className={styles.historyLabel}>
                  {jobsQuery.isLoading ? '生成履歴を取得中…' : '生成履歴'}
                </span>
              </button>
              <div
                className={`${styles.historyPanelContent} ${showOlderJobs ? styles.historyPanelContentOpen : ''}`}
                aria-hidden={!showOlderJobs}
              >
                <div className={styles.historyPanelContentInner}>
                  {sortedJobs.length > 0 && (
                    <>
                      <div className={styles.historyHeader}>
                        {jobsQuery.isFetching && <div className="spinner spinner-sm" />}
                        <button
                          className={styles.textLink}
                          type="button"
                          onClick={() => handleRefreshJobs()}
                          disabled={jobsQuery.isFetching}
                        >
                          更新
                        </button>
                      </div>
                      <ul className={styles.jobList}>
                        {sortedJobs.map((job) => (
                          <JobListItem
                            key={job.id}
                            job={job}
                            onPreview={handleOpenMaterialModal}
                            onShowInfo={(mid) => setInfoModalMaterialId(mid)}
                            onRestore={handleRestoreSettings}
                          />
                        ))}
                      </ul>
                      {jobsHasMore && (
                        <button
                          type="button"
                          className={styles.loadMoreButton}
                          onClick={handleLoadMoreJobs}
                          disabled={jobsQuery.isFetching}
                        >
                          {jobsQuery.isFetching ? '読み込み中…' : 'もっと見る ▾'}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`${styles.historyToggle} ${styles.historyToggleExpanded}`}
                        onClick={() => setShowOlderJobs(false)}
                      >
                        <img src="/unfold_less_fill.svg" alt="" className={styles.historyIcon} />
                        <span className={styles.historyLabel}>折りたたむ</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 素材モーダル */}
      {materialModalId && materialQuery.data ? (
        <MaterialPreviewModal
          material={materialQuery.data}
          materialId={materialModalId}
          onClose={handleCloseMaterialModal}
          showEditControls={true}
          showAddToSelectionButton={false}
          showShareButton={false}
          isTemporary={true}
          onRequestRefresh={async () => {
            // 生成履歴を即座にリフレッシュ
            await handleRefreshJobs();
            queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
            // materialQueryは削除された素材なのでリフレッシュしない
          }}
        />
      ) : null}

      {/* AI生成情報モーダル（生成直後プレビュー用） */}
      {showGenerationInfo && lastJobMaterial && (
        <GenerationInfoModal
          material={lastJobMaterial}
          fullSizeImageUrl={generatedImageSrc ?? null}
          onClose={() => setShowGenerationInfo(false)}
        />
      )}

      {/* AI生成情報モーダル（生成履歴からの呼び出し） */}
      {infoModalMaterialId && (() => {
        const cached = queryClient.getQueryData<TemporaryMaterial>([
          'temporary-material',
          infoModalMaterialId,
        ]);
        if (!cached) return null;
        return (
          <GenerationInfoModal
            material={cached}
            fullSizeImageUrl={null}
            onClose={() => setInfoModalMaterialId(null)}
          />
        );
      })()}

      {/* Stickyフローティングボタン */}
      {showStickyButton && (
        <div className={`${styles.stickyButtonContainer} ${activeJobCount > 0 ? styles.stickyButtonWithStatusBar : ''}`}>
          <div className={styles.stickyButtonInner}>
            <div className={styles.stickyLeft}>
              {activeJobCount === 0 && activeJobs.length > 0 && (
                <button
                  type="button"
                  className={styles.stickyCompletedBtn}
                  onClick={() => setShowJobQueueModal(true)}
                >
                  ✓ 生成完了
                </button>
              )}
            </div>

            <div className={styles.stickyCenter}>
              <GenerateButton
                label={lastJob || activeJobs.length > 0 ? '画像を再生成' : '画像を生成'}
                creditCost={creditBadgeText}
                onClick={handleGenerate}
                isLoading={jobMutation.isPending || isCapturing}
                loadingLabel={isCapturing ? 'キャプチャ中' : '送信中'}
                showLoadingDots
                disabled={
                  jobMutation.isPending
                  || isCapturing
                  || isInputMissing
                  || isTemplateReferenceMissing
                  || !!bannerLayoutIssue
                  || isCreditInsufficient
                  || isStorageExceeded
                  || isProductInfoMissing
                }
              />
            </div>

            <div className={styles.stickyRight}>
              {guidanceMessages.length > 0 && (
                <div className={styles.stickyGuidance}>
                  {guidanceMessages.map((msg, idx) => (
                    <div key={idx}>{msg}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {purchaseModals}
    </>
  );
}

function JobListItem({
  job,
  onPreview,
  onShowInfo,
  onRestore,
}: {
  job: GenerationJob;
  onPreview: (materialId?: string) => void;
  onShowInfo: (materialId: string) => void;
  onRestore: (genMeta: Record<string, unknown>) => void;
}) {
  const queryClient = useQueryClient();
  const createdAt = useMemo(() => {
    const date = new Date(job.created_at);
    const yyyy = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const hh = `${date.getHours()}`.padStart(2, '0');
    const mm = `${date.getMinutes()}`.padStart(2, '0');
    const ss = `${date.getSeconds()}`.padStart(2, '0');
    return `${yyyy}/${m}/${d} ${hh}:${mm}:${ss}`;
  }, [job.created_at]);
  const previewSrc = job.thumbnail_url;
  const isDeleted = job.is_deleted || !job.material_id;
  const isFailed = job.status === 'failed';
  const statusText = job.status === 'completed' ? '完了' : job.status === 'failed' ? '失敗' : job.status;
  const displayablePrompt = useMemo(() => {
    const ui = job.user_inputs;
    if (!ui) return '';
    if (ui.mode === 'prompt') return (ui.prompt ?? '').trim();
    if (ui.mode === 'attributes') return (ui.additional_instruction ?? '').trim();
    return '';
  }, [job.user_inputs]);

  // 媒体要件・AI生成情報のために material を取得（完了済かつ未削除のみ）
  const materialQuery = useQuery({
    queryKey: ['temporary-material', job.material_id],
    queryFn: async () => {
      const cached = queryClient.getQueryData<{ items: TemporaryMaterial[] }>(['materials', 'temporary']);
      const found = cached?.items?.find(m => m.id === job.material_id);
      if (found) return found;
      return fetchTemporaryMaterial(job.material_id!);
    },
    enabled: !!job.material_id && !isDeleted && job.status === 'completed',
    staleTime: 60_000,
  });
  const material = materialQuery.data as Record<string, unknown> | undefined;
  const genMeta = material?.generation_metadata as Record<string, unknown> | undefined;
  const hasInfo = hasGenerationInfo(genMeta);

  // generation_models マスタからモデル日本語名を解決
  const { data: imageModels = [] } = useGenerationModels('image');
  const { data: videoModels = [] } = useGenerationModels('video');
  const modelLabel = useMemo(() => {
    if (!job.model) return null;
    const all = [...imageModels, ...videoModels];
    // 完全一致 → 前方一致（resource_type には -2k や -1080p-with-audio が付く場合がある）
    const exact = all.find((m) => m.model_code === job.model);
    if (exact) return exact.display_label;
    const prefix = all
      .filter((m) => job.model!.startsWith(m.model_code))
      .sort((a, b) => b.model_code.length - a.model_code.length)[0];
    return prefix ? prefix.display_label : job.model;
  }, [job.model, imageModels, videoModels]);

  // 媒体要件バッジ用にラベルを抽出
  const reqLabels = useMemo<{ label: string; value: string }[]>(() => {
    if (!material) return [];
    const items: { label: string; value: string }[] = [];
    const width = material.width as number | undefined;
    const height = material.height as number | undefined;
    const aspect =
      calculateAspectRatio(width, height) || (genMeta?.aspect_ratio as string | undefined);
    if (aspect) items.push({ label: 'アスペクト比', value: aspect });
    if (width && height) items.push({ label: '解像度', value: `${width}×${height}` });
    if (modelLabel) items.push({ label: '生成モデル', value: modelLabel });
    return items;
  }, [material, genMeta, modelLabel]);

  // 属性指定モードの場合、選択された属性をラベルとして表示
  const { data: attributesData } = usePromptAttributes();
  const { attrTags, attrTexts } = useMemo(() => {
    const parsed = parseGenerationMetadata(genMeta ?? null);
    if (!parsed || parsed.mode !== 'attributes') return { attrTags: [], attrTexts: [] };
    const items = resolveAttributeLabels(
      parsed.attributes,
      attributesData?.items as AttributeNode[] | undefined,
      { productTextGenAsLabel: true },
    );
    const tags: { label: string; value: string }[] = [];
    const texts: { label: string; value: string }[] = [];
    for (const item of items) {
      if (TEXT_ATTRIBUTE_KEYS.has(item.key)) {
        texts.push({ label: item.label, value: item.value });
      } else {
        tags.push({ label: item.label, value: item.value });
      }
    }
    return { attrTags: tags, attrTexts: texts };
  }, [genMeta, attributesData]);

  return (
    <li className={styles.jobItem}>
      {isFailed ? (
        <div className={styles.jobThumbnailPlaceholder} title={job.error_message || 'エラーが発生しました'}>
          <span className={styles.deletedText}>エラー</span>
        </div>
      ) : previewSrc && !isDeleted ? (
        <button
          type="button"
          className={styles.jobThumbnailButton}
          onClick={() => onPreview(job.material_id)}
          aria-label={`ジョブ ${job.id} の画像を拡大表示`}
        >
          <img src={previewSrc} alt="生成サムネイル" />
        </button>
      ) : (
        <div className={styles.jobThumbnailPlaceholder}>
          <span className={styles.deletedText}>削除されました</span>
        </div>
      )}
      <div className={styles.jobBody}>
        {attrTags.length > 0 && (
          <div className={styles.jobAttrTags}>
            {attrTags.map((attr) => (
              <Tooltip key={attr.value} content={attr.label} placement="bottom">
                <span className={styles.jobAttrTag}>{attr.value}</span>
              </Tooltip>
            ))}
          </div>
        )}
        {attrTexts.length > 0 && (
          <div className={styles.jobAttrTexts}>
            {attrTexts.map((t) => (
              <p key={t.label} className={styles.jobPrompt} title={t.value}>
                <span className={styles.jobTextLabel}>{t.label}</span>{t.value}
              </p>
            ))}
          </div>
        )}
        {attrTags.length === 0 && attrTexts.length === 0 && (
          <p className={styles.jobPrompt} title={displayablePrompt}>
            {displayablePrompt ? (
              <><span className={styles.jobTextLabel}>プロンプト</span>{displayablePrompt}</>
            ) : (
              isFailed ? '' : '（プロンプト情報なし）'
            )}
          </p>
        )}
        <div className={styles.jobReqs}>
          {reqLabels.map((req) => (
            <Tooltip key={req.value} content={req.label} placement="bottom">
              <span className={styles.jobReqBadge}>{req.value}</span>
            </Tooltip>
          ))}
        </div>
        <div className={styles.jobBodyFooter}>
          <span className={styles.jobTimestamp}>{createdAt}</span>
          {hasInfo && job.material_id && (
            <button
              type="button"
              className={styles.jobInfoLink}
              onClick={() => onShowInfo(job.material_id!)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              AI生成情報
            </button>
          )}
          {(genMeta || (job.user_inputs?.mode === 'prompt' && displayablePrompt)) && (
            <Tooltip content="この設定を復元" placement="bottom" className={styles.jobRestoreTooltip}>
              <button
                type="button"
                className={styles.jobRestoreButton}
                onClick={() => genMeta
                  ? onRestore(genMeta)
                  : onRestore({ _app: { mode: 'prompt', user_inputs: { prompt: displayablePrompt } } })}
              >
                <img src="/navigation_direction_left_forward.svg" alt="" width="14" height="14" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <span className={`${job.status === 'completed' ? styles.badge : job.status === 'failed' ? styles.badgeError : styles.badgePending} ${styles.jobStatus}`}>
        {statusText}
      </span>
    </li>
  );
}
